import { Alert, Box, Card, HStack, Heading, Text } from '@chakra-ui/react'
import type React from 'react'
import type { PropsWithChildren, ReactNode } from 'react'

type SectionCardProps = {
  /**
   * Title of the card element
   */
  title?: ReactNode
  /**
   * Description that will be displayed below the title
   */
  description?: ReactNode
  /**
   * Append below the CardBody useful for full width tables
   */
  append?: ReactNode
  /**
   * Add actions to the card header on the right hand side, useful for button actions
   */
  actions?: ReactNode
  /**
   * Alert description that will be displayed at the top of the card
   */
  alertDescription?: ReactNode
  /**
   * Alert status that will be used to color the alert
   * @default 'info'
   */
  alertStatus?: 'info' | 'warning' | 'success' | 'error'
  titleSize?: 'default' | 'small'
} & Omit<React.ComponentProps<typeof Card.Root>, 'title'>

const alertColors: Record<
  NonNullable<SectionCardProps['alertStatus']>,
  string
> = {
  error: 'red.500',
  info: 'blue.500',
  success: 'green.500',
  warning: 'yellow.500',
}

export function SectionCard({
  title,
  description,
  children,
  append,
  actions,
  alertStatus = 'info',
  alertDescription,
  titleSize = 'default',
  ...cardProps
}: Readonly<PropsWithChildren<SectionCardProps>>) {
  return (
    <Card.Root
      w="100%"
      variant="outline"
      borderColor={alertDescription ? alertColors[alertStatus] : 'inherit'}
      overflow="hidden"
      {...cardProps}
    >
      {alertDescription ? (
        <Alert.Root
          status={alertStatus}
          variant="subtle"
          flexDirection="row"
          alignItems="start"
          justifyContent="start"
          textAlign="left"
          w="100%"
          px={5}
        >
          <Alert.Indicator />
          <Alert.Description fontSize="sm">
            {alertDescription}
          </Alert.Description>
        </Alert.Root>
      ) : null}

      {title && (
        <Card.Header mb={0} pb={0} pt={alertDescription ? 4 : 5}>
          <HStack w="100%" justify="space-between" alignItems="center">
            <Box>
              {typeof title === 'string' ? (
                <Heading
                  as={titleSize === 'small' ? 'h4' : 'h2'}
                  fontSize={titleSize === 'small' ? 'md' : 'xl'}
                  m={0}
                  p={0}
                  display="flex"
                  flexDir="row"
                  gap={1}
                  alignItems="center"
                >
                  {title}
                </Heading>
              ) : (
                title
              )}
              {description ? (
                <Text m={0} mt={0.5}>
                  {description}
                </Text>
              ) : null}
            </Box>

            {actions ? <HStack gap={2}>{actions}</HStack> : null}
          </HStack>
        </Card.Header>
      )}

      {children ? <Card.Body>{children}</Card.Body> : null}

      {append ? <Box mt={3}>{append}</Box> : null}
    </Card.Root>
  )
}
